<script>
export default {
    name: 'Diff',

    props: {
        data: Object,
    },

    computed: {
        diffList() {
            if (!this.data || this.data === 'null') {
                return [{ content: '   --    ', nested: 0 }];
            }

            const data = this.data;
            let lines = [];

            if (data.before && data.after) {
                lines = lines.concat(
                    this.getObjectDiff(data.before, 1, data.after),
                );
            } else {
                lines = lines.concat(this.getObjectDiff(data));
            }

            return lines;
        },
    },

    methods: {
        getObjectDiff(before, nestLevel = 0, after = null) {
            if (!before) {
                return [];
            }

            let lines = [];

            if (before && after) {
                const all = [...Object.keys(before)];
                const afterProps = Object.keys(after);

                // Make sure not to duplicate keys in the diff
                for (const key of afterProps) {
                    if (!all.includes(key)) {
                        all.push(key);
                    }
                }

                for (const prop of all.sort()) {
                    // We don't currently need to handle a case where we have a diff property nested object
                    // The function is expecting after to be null if there is no diff, so if there is
                    //  one, we need to make sure to specify
                    // Check to see if the value of the prop has changed
                    if (before[prop] !== after[prop]) {
                        // If it had a value before, show it as deleted
                        if (this.isValidValue(before[prop])) {
                            lines.push({
                                deleted: true,
                                content: `${prop}: ${before[prop]}`,
                                nested: nestLevel,
                            });
                        }

                        // If the value hasn't been removed, show the new value
                        if (this.isValidValue(after[prop])) {
                            lines.push({
                                added: true,
                                content: `${prop}: ${after[prop]}`,
                                nested: nestLevel,
                            });
                        }
                    } else {
                        // If the line hasn't changed, don't color it
                        lines.push({
                            content: `${prop}: ${before[prop]}`,
                            nested: nestLevel,
                        });
                    }
                }
            } else {
                // We don't have a diff, just display the data in this object
                for (const prop of Object.keys(before).sort()) {
                    // Special case call for object property with next nest level
                    if (typeof before[prop] === 'object') {
                        lines.push({
                            content: `${prop}:`,
                            nested: nestLevel,
                        });
                        lines = lines.concat(
                            this.getObjectDiff(before[prop], nestLevel + 1),
                        );
                    } else {
                        lines.push({
                            content: `${prop}: ${before[prop]}`,
                            nested: nestLevel,
                        });
                    }
                }
            }

            return lines;
        },

        isValidValue(value) {
            return value || value === 0 || value === '' || value === false;
        },
    },
};
</script>

<template>
    <div class="data">
        <div
            v-for="(line, index) in diffList"
            :key="index"
            class="line"
            :class="{ added: line.added, deleted: line.deleted }"
        >
            <div class="diff-symbol" v-if="line.added || line.deleted">
                {{ line.added ? '+' : '-' }}
            </div>
            <p
                class="text"
                :class="{
                    bracket: index === 0 || index === diffList.length - 1,
                }"
                :style="{ 'margin-left': `${20 + line.nested * 18}px` }"
            >
                {{ line.content }}
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.data {
    margin-bottom: 0;
    font-family: 'Consolas', monospace;
    font-size: 0.8em;
    display: flex;
    flex-direction: column;
    width: 100%;

    .line {
        position: relative;
        display: flex;
        align-items: center;
        height: 20px;

        &.added {
            background-color: color.adjust(
                color.adjust($theme-green, $alpha: -0.85),
                $lightness: 15%
            );
        }

        &.deleted {
            background-color: color.adjust(
                color.adjust($theme-red, $alpha: -0.85),
                $lightness: 15%
            );
        }

        .text {
            white-space: nowrap;
            height: 100%;
            margin-left: 38px;
            margin-bottom: 0;
            width: 100%;
            text-overflow: ellipsis;
            overflow: hidden;
            padding-right: 8px;
            padding-top: 3px;

            &.nested {
                margin-left: 52px;
            }

            &.bracket {
                margin-left: 20px;
            }
        }

        .diff-symbol {
            position: absolute;
            height: 20px;
            width: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: transparent;
            color: $theme-grey;
            padding-bottom: 1px;
        }
    }
}
</style>
